import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { CustomBreadcrumb } from '@/shared/components/atoms';
import { CustomCommonScreenHeader, CustomTable, CustomTableColumn, ConfirmationModal } from '@/shared/components/molecules';
import { Home, Globe, Building2, School, Trash2 } from 'lucide-react';
import { schoolApi } from '../api/schoolApi';
import { School as SchoolType } from '../types';
import { useToastStore } from '@/shared/stores/toastStore';
import { getMediaUrl } from '@/shared/utils/media';

export const ListSchools = () => {
    const navigate = useNavigate();
    const [schools, setSchools] = useState<SchoolType[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [pageSize] = useState(10);
    const [total, setTotal] = useState(0);
    const [hasNext, setHasNext] = useState(false);
    const [hasPrevious, setHasPrevious] = useState(false);
    const { showToast } = useToastStore();

    // Toggling state: track which school IDs are currently being toggled
    const [togglingIds, setTogglingIds] = useState<Set<string>>(new Set());

    // Delete modal state
    const [deleteTarget, setDeleteTarget] = useState<SchoolType | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);

    const fetchSchools = useCallback(async () => {
        setIsLoading(true);
        try {
            const response = await schoolApi.getSchools({ page, page_size: pageSize });
            if (response.success && response.data) {
                setSchools(response.data.items);
                setTotal(response.data.total);
                setHasNext(response.data.has_next);
                setHasPrevious(response.data.has_previous);
            }
        } catch (error) {
            showToast('Failed to load schools', 'error');
        } finally {
            setIsLoading(false);
        }
    }, [page, pageSize, showToast]);

    useEffect(() => {
        fetchSchools();
    }, [fetchSchools]);

    const totalPages = Math.max(1, Math.ceil(total / pageSize));

    const handleToggleStatus = async (e: React.MouseEvent, school: SchoolType) => {
        e.stopPropagation();
        const newStatus = !school.is_active;
        setTogglingIds(prev => new Set(prev).add(school.id));
        try {
            const response = await schoolApi.toggleSchoolStatus(school.id, newStatus);
            if (response.success) {
                showToast(response.message || `School ${newStatus ? 'activated' : 'deactivated'}`, 'success');
                // Optimistically update local state
                setSchools(prev => prev.map(s => s.id === school.id ? { ...s, is_active: newStatus } : s));
            } else {
                showToast(response.error || 'Failed to update status', 'error');
            }
        } catch {
            showToast('An unexpected error occurred', 'error');
        } finally {
            setTogglingIds(prev => { const next = new Set(prev); next.delete(school.id); return next; });
        }
    };

    const handleDeleteClick = (e: React.MouseEvent, school: SchoolType) => {
        e.stopPropagation();
        setDeleteTarget(school);
    };

    const handleDeleteConfirm = async () => {
        if (!deleteTarget) return;
        setIsDeleting(true);
        try {
            const response = await schoolApi.deleteSchool(deleteTarget.id);
            if (response.success) {
                showToast(response.message || 'School deleted successfully', 'success');
                setDeleteTarget(null);
                fetchSchools();
            } else {
                showToast(response.error || 'Failed to delete school', 'error');
            }
        } catch {
            showToast('An unexpected error occurred', 'error');
        } finally {
            setIsDeleting(false);
        }
    };

    const columns: CustomTableColumn<SchoolType>[] = [
        {
            header: 'School Name',
            key: 'name',
            width: '26%',
            minWidth: '200px',
            render: (school) => {
                const logoUrl = getMediaUrl(school.logo_url);
                return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        <div style={{
                            width: '34px',
                            height: '34px',
                            borderRadius: '8px',
                            background: logoUrl ? 'white' : (school.primary_color || 'rgba(99, 102, 241, 0.1)'),
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: '1px solid var(--border-color)',
                            overflow: 'hidden',
                            flexShrink: 0
                        }}>
                            {logoUrl ? (
                                <img src={logoUrl} alt={school.name} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                            ) : (
                                <School size={16} color={school.primary_color || 'var(--accent-primary)'} />
                            )}
                        </div>
                        <div style={{ minWidth: 0, overflow: 'hidden' }}>
                            <div style={{ fontWeight: 600, textTransform: 'capitalize', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>{school.name}</div>
                            <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>/{school.slug}</div>
                        </div>
                    </div>
                );
            }
        },
        {
            header: 'Admin Email',
            key: 'admin_email',
            width: '24%',
            minWidth: '180px',
            render: (school) => (
                <span style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
                    {school.admin_email || 'N/A'}
                </span>
            )
        },
        {
            header: 'Support Contact',
            key: 'support_email' as any,
            width: '20%',
            minWidth: '160px',
            render: (school) => (
                <div>
                    <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>{school.support_email || '—'}</div>
                    {school.support_phone && (
                        <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>{school.support_phone}</div>
                    )}
                </div>
            )
        },
        {
            header: 'Created At',
            key: 'created_at',
            width: '14%',
            minWidth: '110px',
            render: (school) => (
                <span style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
                    {new Date(school.created_at).toLocaleDateString(undefined, {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                    })}
                </span>
            )
        },
        {
            header: 'Actions',
            key: 'id' as any,
            width: '16%',
            minWidth: '140px',
            render: (school) => {
                const isToggling = togglingIds.has(school.id);
                const isActive = school.is_active !== false;

                return (
                    <div className="table-action-cell" onClick={(e) => e.stopPropagation()}>
                        {/* Active / Inactive Toggle */}
                        <button
                            title={isActive ? 'Click to deactivate' : 'Click to activate'}
                            onClick={(e) => handleToggleStatus(e, school)}
                            disabled={isToggling}
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '0.35rem',
                                padding: '0.25rem 0.55rem',
                                borderRadius: '20px',
                                border: 'none',
                                fontSize: '0.7rem',
                                fontWeight: 600,
                                cursor: isToggling ? 'not-allowed' : 'pointer',
                                opacity: isToggling ? 0.6 : 1,
                                transition: 'all 0.2s',
                                background: isActive ? 'rgba(16,185,129,0.12)' : 'rgba(239,68,68,0.1)',
                                color: isActive ? '#10b981' : '#ef4444',
                            }}
                        >
                            {/* Toggle pill track */}
                            <span style={{
                                position: 'relative',
                                display: 'inline-block',
                                width: '28px',
                                height: '15px',
                                borderRadius: '999px',
                                background: isActive ? '#10b981' : '#d1d5db',
                                transition: 'background 0.2s',
                                flexShrink: 0,
                            }}>
                                <span style={{
                                    position: 'absolute',
                                    top: '2px',
                                    left: isActive ? '15px' : '2px',
                                    width: '11px',
                                    height: '11px',
                                    borderRadius: '50%',
                                    background: 'white',
                                    transition: 'left 0.2s',
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                                }} />
                            </span>
                            {isToggling ? '...' : isActive ? 'Active' : 'Inactive'}
                        </button>

                        {/* Delete */}
                        <button
                            title="Delete school"
                            onClick={(e) => handleDeleteClick(e, school)}
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '30px',
                                height: '30px',
                                borderRadius: '7px',
                                border: '1px solid var(--border-color)',
                                background: 'var(--bg-secondary)',
                                color: '#ef4444',
                                cursor: 'pointer',
                                transition: 'all 0.15s',
                            }}
                            onMouseEnter={e => {
                                (e.currentTarget as HTMLButtonElement).style.background = 'rgba(239,68,68,0.08)';
                                (e.currentTarget as HTMLButtonElement).style.borderColor = '#ef4444';
                            }}
                            onMouseLeave={e => {
                                (e.currentTarget as HTMLButtonElement).style.background = 'var(--bg-secondary)';
                                (e.currentTarget as HTMLButtonElement).style.borderColor = 'var(--border-color)';
                            }}
                        >
                            <Trash2 size={14} />
                        </button>
                    </div>
                );
            }
        },
    ];

    return (
        <div className="page-container">
            <CustomBreadcrumb
                items={[
                    { label: 'Home', path: '/', icon: <Home size={14} /> },
                    { label: 'Manage Schools', icon: <Globe size={14} /> }
                ]}
            />
            <CustomCommonScreenHeader
                title="School Management"
                subtitle="Add or view school instances."
                icon={<Globe size={24} />}
                actionLabel="Add School"
                onActionClick={() => navigate('/super-admin/tenants/create')}
            />

            <div style={{ marginTop: '1.25rem' }}>
                <CustomTable
                    columns={columns}
                    data={schools}
                    isLoading={isLoading}
                    isCompact={true}
                    emptyMessage="No schools found"
                    emptyIcon={<Building2 size={32} />}
                    onRowClick={(school) => navigate(`/super-admin/tenants/${school.slug}`)}
                    pagination={{
                        currentPage: page,
                        totalPages: totalPages,
                        totalCount: total,
                        pageSize: pageSize,
                        onPageChange: setPage,
                        hasPrevious: hasPrevious,
                        hasNext: hasNext,
                        isLoading: isLoading,
                        itemName: 'schools',
                    }}
                />
            </div>

            {/* Delete Confirmation Modal */}
            <ConfirmationModal
                isOpen={!!deleteTarget}
                onClose={() => setDeleteTarget(null)}
                onConfirm={handleDeleteConfirm}
                title="Delete School"
                message={`Are you sure you want to delete "${deleteTarget?.name}"? This action cannot be undone and will remove all associated data.`}
                confirmLabel={isDeleting ? 'Deleting...' : 'Delete'}
                variant="danger"
                isLoading={isDeleting}
            />
        </div>
    );
};
